'use client';
import { useFormikContext, FormikContextType } from 'formik';

import AddressInputField from '@/components/form/lib/InputField/AddressInputField';
import InputField from '@/components/form/lib/InputField/InputField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';
import Select from '@/components/form/lib/InputField/Select';
import Text from '@/components/common/Text/Text';

import { US_STATES } from '@/util/user_client';

import { ApplicationFormValues } from './applicationValidation';

const ZIP_MASK = [/\d/, /\d/, /\d/, /\d/, /\d/];

/**
 * Splits a Google-autocomplete formatted address into its parts.
 * e.g. "1 Shipping Way, Shipville, NY 10001, USA"
 */
function parseFormattedAddress(formatted: string) {
  const parts = (formatted || '').split(',');
  const regionAndPostcode = parts[2]?.trim().split(' ') ?? [];

  return {
    line1: parts[0] || '',
    city: parts[1]?.trim() || '',
    region: regionAndPostcode[0] || '',
    postcode: regionAndPostcode[1] || ''
  };
}

/**
 * "Shipping" — the only address the V2 design collects. Billing is derived
 * from it at submit time (`applyBillingMirror` in `addressSync.ts`).
 */
function ApplicationShippingFields() {
  const { values } = useFormikContext<ApplicationFormValues>();

  const handleShippingSelected = (
    formikContext: FormikContextType<unknown>,
    formattedAddress: string
  ) => {
    const { city, region, postcode } = parseFormattedAddress(formattedAddress);

    formikContext.setFieldValue('city', city, false);
    formikContext.setFieldValue('state', region, false);
    formikContext.setFieldValue('postalCode', postcode, false);
  };

  return (
    <div className='ApplicationShippingFields'>
      <AddressInputField
        className='Signup-address'
        label='Shipping Address*'
        onAddressSelected={handleShippingSelected}
        name='address'
        defaultValue={values.address}
      />
      <div className='Signup-row'>
        <InputField
          className='Signup-apt-unit-suite'
          name='aptUnitSuite'
          autoComplete='shipping address-line2'
          label='apt/unit/suite (optional)'
          type='text'
        />
        <InputField
          className='Signup-city'
          name='city'
          autoComplete='shipping address-level2'
          label='City'
          type='text'
        />
        <Select
          name='state'
          autoComplete='shipping address-level1'
          label='State'
          options={US_STATES}
        />
        <MaskedField
          id='postalCode'
          className='Signup-postal-code'
          guide={false}
          mask={ZIP_MASK}
          label='Postal Code'
          name='postalCode'
          autoComplete='shipping postal-code'
        />
      </div>

      <Text
        className='Signup-footnote'
        variant='text-s'
      >
        *Please note PO Boxes are not accepted
      </Text>
    </div>
  );
}

export default ApplicationShippingFields;
